import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'

import { GET, POST, httpOk } from 'helpers/api'
import { notify } from 'helpers/helpers'
import { smsInfo as smsTextInfo } from 'helpers/sms'
import { parseChatId, parseThreadId, parseTelegramLink, validateTelegramTarget } from 'helpers/telegram'
import useUnsavedChangesGuard from 'pages/parts/useUnsavedChangesGuard'
import HintIcon from 'pages/parts/HintIcon'

function Index() {
	const { t } = useTranslation()

	const [channels, setChannels] = useState([])
	const [events, setEvents] = useState([])
	const [categoryPurpose, setCategoryPurpose] = useState({})
	const [poses, setPoses] = useState([])              // филиалы бренда
	const [posGroups, setPosGroups] = useState({})      // purpose -> { pos_id | 'all' -> { chat_id, thread_id } }

	// Правки тумблеров копятся локально: "event_code|channel" -> bool.
	// В матрице щёлкают десятками, поэтому шлём одну дельту, а не запрос на каждый тумблер.
	const [pending, setPending] = useState({})
	const [saving, setSaving] = useState(false)

	// модалка получателей
	const [showRecipModal, setShowRecipModal] = useState(false)
	const [recipEvent, setRecipEvent] = useState(null)
	const [recipUsers, setRecipUsers] = useState([])

	// платный канал: провайдер, цена сегмента и остаток баланса точки
	const [sms, setSms] = useState({})

	// модалка текстов SMS гостю
	const [showSmsModal, setShowSmsModal] = useState(false)
	const [smsEvent, setSmsEvent] = useState(null)
	const [smsTemplates, setSmsTemplates] = useState([])   // [{ locale, body, default }]
	const [smsLocale, setSmsLocale] = useState('ru')

	// модалка Telegram-групп: таблица «филиал × chat_id», правится и сохраняется целиком
	const [showGroupModal, setShowGroupModal] = useState(false)
	const [groupPurpose, setGroupPurpose] = useState(null)
	const [groupRows, setGroupRows] = useState([])      // [{ key, pos_id, name, chat_id, thread_id }]
	const [groupTestingKey, setGroupTestingKey] = useState(null)

	// модалка параметров события (например, «через сколько дней напомнить о долге»)
	const [showParamsModal, setShowParamsModal] = useState(false)
	const [paramsEvent, setParamsEvent] = useState(null)
	const [paramsDraft, setParamsDraft] = useState({})

	const pendingKeys = Object.keys(pending)
	const hasChanges = pendingKeys.length > 0

	useUnsavedChangesGuard(hasChanges, { 'message': t('unsaved_changes_confirm') })

	const channelLabels = {
		'bell': t('channel_bell'),
		'firebase': t('channel_firebase'),
		'tg_group': t('channel_tg_group'),
		'tg_client': t('channel_tg_client'),
		'app_client': t('channel_app_client'),
		'sms': t('channel_sms'),
	}

	// Гостевые каналы: push в приложение бренда, Telegram-бот и SMS
	const CLIENT_CHANNELS = ['app_client', 'tg_client', 'sms']

	// Гостю доступны только его каналы, сотрудникам — свои.
	// Тумблер на неподходящем пересечении не рисуем: он всё равно ничего не делает.
	function channelApplies(event, channel) {
		if (event.recipient_type === 'client') return CLIENT_CHANNELS.includes(channel)
		return channel !== 'tg_client' && channel !== 'app_client'
	}

	async function getData() {
		const response = await GET('/notification-settings', {}, { loader: true })
		if (httpOk(response)) {
			setChannels(response.data.channels)
			setEvents(response.data.events)
			setCategoryPurpose(response.data.category_purpose || {})
			setPoses(response.data.poses || [])
			setPosGroups(response.data.pos_groups || {})
			setSms(response.data.sms || {})
			setPending({})
		}
	}

	// --- Матрица событие × канал ---
	function cellKey(eventCode, channel) {
		return eventCode + '|' + channel
	}

	// Что показывать в тумблере: несохранённая правка, иначе значение с сервера
	function cellValue(event, channel) {
		const key = cellKey(event.code, channel)
		return key in pending ? pending[key] : !!event.channels[channel]
	}

	function toggle(event, channel, enabled) {
		const key = cellKey(event.code, channel)
		setPending(prev => {
			const next = { ...prev }
			// Вернули как было — правка исчезает, чтобы счётчик не врал
			if (!!event.channels[channel] === enabled) delete next[key]
			else next[key] = enabled
			return next
		})
	}

	async function saveChanges() {
		if (!hasChanges || saving) return

		// Снимок правок: пока запрос в пути, пользователь может щёлкнуть ещё —
		// такие правки не должны пропасть при очистке
		const sent = { ...pending }
		const changes = Object.keys(sent).map(key => {
			const separator = key.lastIndexOf('|')
			return {
				'event_code': key.slice(0, separator),
				'channel': key.slice(separator + 1),
				'enabled': sent[key],
			}
		})

		setSaving(true)
		const response = await POST('/notification-settings/toggle-bulk', { 'changes': changes }, { loader: true, notification: true })
		setSaving(false)
		if (!httpOk(response)) return

		setEvents(prev => prev.map(e => {
			const updated = { ...e.channels }
			let touched = false
			Object.keys(updated).forEach(channel => {
				const key = cellKey(e.code, channel)
				if (key in sent) {
					updated[channel] = sent[key]
					touched = true
				}
			})
			return touched ? { ...e, 'channels': updated } : e
		}))
		setPending(prev => {
			const next = { ...prev }
			Object.keys(sent).forEach(key => {
				if (next[key] === sent[key]) delete next[key]
			})
			return next
		})
	}

	// Ctrl+S — привычный жест для формы с явным сохранением
	const saveChangesRef = useRef(saveChanges)
	saveChangesRef.current = saveChanges

	useEffect(() => {
		function onKeyDown(e) {
			// code, а не key — иначе на русской раскладке сочетание не ловится
			if (!(e.ctrlKey || e.metaKey) || e.code !== 'KeyS') return
			e.preventDefault()
			saveChangesRef.current()
		}
		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
	}, [])

	// Сколько филиалов получают уведомления категории: своя группа или общая по бренду
	function groupStats(purpose) {
		const map = posGroups[purpose] || {}
		const hasAll = !!(map['all'] && map['all'].chat_id)
		const covered = poses.filter(pos => {
			const own = map[String(pos.id)]
			return !!(own && own.chat_id) || hasAll
		}).length
		return { 'covered': covered, 'total': poses.length, 'hasAll': hasAll }
	}

	// Считаем по той же таблице, что показывает модалка: второй источник (адрес текущей точки
	// из матрицы) после сохранения групп оставался старым, и бейдж говорил «Настроено 0/2»
	function groupConfigured(purpose) {
		const stats = groupStats(purpose)
		return stats.covered > 0 || stats.hasAll
	}

	// --- Telegram-группы категории по филиалам ---
	// Строки собираем на каждое открытие: первая — общая группа бренда (pos_id = null),
	// дальше по филиалу. Общая сверху, потому что филиал без своей группы падает на неё.
	function openGroupModal(purpose) {
		const map = posGroups[purpose] || {}
		const buildRow = (key, posId, name) => {
			const g = map[key] || {}
			return {
				'key': key,
				'pos_id': posId,
				'name': name,
				'chat_id': g.chat_id || '',
				'thread_id': g.thread_id || '',
			}
		}

		setGroupPurpose(purpose)
		setGroupRows([buildRow('all', null, t('all_branches'))].concat(
			poses.map(pos => buildRow(String(pos.id), pos.id, pos.name))
		))
		setGroupTestingKey(null)
		setShowGroupModal(true)
	}

	function updateGroupRow(key, patch) {
		setGroupRows(prev => prev.map(row => row.key === key ? { ...row, ...patch } : row))
	}

	// Вставили ссылку на группу — раскладываем её по обоим полям строки. Обычный ввод цифр
	// парсер не трогает: parseTelegramLink срабатывает только на ссылку.
	function handleChatIdChange(row, value) {
		const link = parseTelegramLink(value)
		if (link.chat_id) {
			updateGroupRow(row.key, {
				'chat_id': link.chat_id,
				'thread_id': link.thread_id ? String(link.thread_id) : row.thread_id,
			})
			return
		}
		updateGroupRow(row.key, { 'chat_id': value })
	}

	// В поле темы тоже вставляют ссылку целиком — берём из неё номер темы
	function handleThreadIdChange(row, value) {
		const link = parseTelegramLink(value)
		if (link.thread_id) {
			updateGroupRow(row.key, {
				'chat_id': row.chat_id || link.chat_id || '',
				'thread_id': String(link.thread_id),
			})
			return
		}
		updateGroupRow(row.key, { 'thread_id': value })
	}

	// Нормализованная пара строки (null, если ввод не проходит проверку).
	// В сообщении называем филиал: иначе непонятно, какая из строк таблицы виновата.
	function normalizedRowTarget(row) {
		const chatId = parseChatId(row.chat_id)
		const threadId = parseThreadId(row.thread_id)

		const check = validateTelegramTarget(chatId, threadId, row.thread_id)
		if (!check.ok) {
			notify(row.name + ': ' + t(check.error))
			return null
		}

		return { 'chat_id': chatId, 'thread_id': threadId ? String(threadId) : '' }
	}

	// Сохранение всей таблицы одним запросом: сервер пишет её в транзакции,
	// поэтому принимаются либо все филиалы, либо ни один.
	async function saveGroups() {
		const items = []
		for (const row of groupRows) {
			// Пустой Chat ID — это снятие настройки филиала, проверять нечего
			if (String(row.chat_id).trim() === '') {
				items.push({ 'pos_id': row.pos_id, 'chat_id': '', 'thread_id': '' })
				continue
			}

			const target = normalizedRowTarget(row)
			if (!target) return

			items.push({ 'pos_id': row.pos_id, 'chat_id': target.chat_id, 'thread_id': target.thread_id })
		}

		const response = await POST('/notification-settings/groups', {
			'purpose': groupPurpose, 'items': items,
		}, { loader: true, notification: true })
		if (!httpOk(response)) return

		// Состояние берём из ответа: в таблице должно остаться то, что реально лежит в базе
		setPosGroups(prev => ({ ...prev, [groupPurpose]: response.data.groups || {} }))
		setShowGroupModal(false)
	}

	// Проверка связи по одной строке: бот в группе, темы включены, тема существует.
	// Сервер шлёт в группу тестовое сообщение — оно и есть подтверждение.
	async function testGroupRow(row) {
		const target = normalizedRowTarget(row)
		if (!target) return

		setGroupTestingKey(row.key)
		const response = await POST('/notification-settings/group/test', {
			'chat_id': target.chat_id,
			'thread_id': target.thread_id,
		}, { notification: false })
		setGroupTestingKey(null)

		if (httpOk(response)) {
			updateGroupRow(row.key, { 'chat_id': target.chat_id, 'thread_id': target.thread_id })
			notify(row.name + ': ' + response.data.message, 'success')
		}
	}

	// --- Тексты SMS гостю ---
	async function openSmsTemplates(event) {
		setSmsEvent(event)
		setSmsTemplates([])
		setSmsLocale('ru')
		setShowSmsModal(true)
		const response = await GET(`/notification-settings/sms-templates?event_code=${event.code}`, {}, { loader: true })
		if (httpOk(response)) {
			setSmsTemplates(response.data.templates || [])
		}
	}

	function smsTemplateBody(locale) {
		const row = smsTemplates.find(item => item.locale === locale)
		return row ? row.body : ''
	}

	function smsTemplateDefault(locale) {
		const row = smsTemplates.find(item => item.locale === locale)
		return row ? row.default : ''
	}

	// Цена шаблона: сегменты × цена сегмента у этой точки
	function smsPriceHint(text) {
		const info = smsTextInfo(text)
		if (!sms.price) return `${info.length} / ${info.segments} SMS`
		return `${info.length} → ${info.segments} SMS × ${sms.price} = ${info.segments * sms.price}`
	}

	// --- Параметры события ---
	// Сохраняются сразу из модалки, мимо панели правок: это не тумблер канала,
	// и смешивать их в одном счётчике «несохранённых» незачем
	function openParams(event) {
		setParamsEvent(event)
		setParamsDraft({ ...(event.params || {}) })
		setShowParamsModal(true)
	}

	// Час отправки — не числовое поле, а выбор «10:00». Шаг — час: крон напоминаний
	// ходит раз в час, минуты всё равно не соблюсти.
	function isHourParam(name) {
		return name === 'send_hour'
	}

	function paramHours(spec) {
		return Array.from({ length: spec.max - spec.min + 1 }, (item, index) => spec.min + index)
	}

	function hourLabel(hour) {
		return String(hour).padStart(2, '0') + ':00'
	}

	// Живой пример к «Напоминанию о долге»: гость купил в долг сегодня в 14:00, дальше —
	// даты напоминаний по введённым числам. Правила те же, что у крона
	// (ClientDebtNotificationService::remindDue): срок в календарных днях, отправка
	// в час точки (send_hour), не больше max_count раз.
	function formatPreviewDate(date) {
		const label = String(date.getDate()).padStart(2, '0') + '.' + String(date.getMonth() + 1).padStart(2, '0')
		return date.getFullYear() !== new Date().getFullYear() ? label + '.' + date.getFullYear() : label
	}

	function debtReminderPreview() {
		const schema = paramsEvent.params_schema || {}
		const inRange = (name) => {
			const value = Number(paramsDraft[name])
			return Number.isInteger(value) && value >= schema[name].min && value <= schema[name].max
		}
		// Пока число не введено целиком или вне границ — пример не рисуем, а не показываем чушь
		if (!inRange('days') || !inRange('max_count')) return null

		const days = Number(paramsDraft.days)
		const count = Number(paramsDraft.max_count)
		const sendTime = 'send_hour' in schema ? hourLabel(paramsDraft.send_hour) : ''

		const purchase = new Date()
		purchase.setHours(14, 0, 0, 0)
		const rows = [{ 'date': purchase, 'time': '14:00', 'text': t('debt_preview_purchase') }]
		for (let number = 1; number <= count; number++) {
			const date = new Date(purchase)
			date.setDate(purchase.getDate() + number * days)
			rows.push({ 'date': date, 'time': sendTime, 'text': t('debt_preview_reminder', { 'number': number }) })
		}

		return (
			<div className="mt-3">
				<div className="fw-600 mb-1" style={{ 'fontSize': '.85rem' }}>
					{t('debt_preview_title')}
					<HintIcon text={t('debt_preview_note')} />
				</div>
				{/* Без своей прокрутки: прокручивается тело модалки, вложенный скролл путает */}
				<div>
					<table className="table table-sm table-bordered mb-0" style={{ 'fontSize': '.82rem' }}>
						<thead>
							<tr>
								<th style={{ 'width': '120px' }}>{t('debt_preview_when')}</th>
								<th>{t('debt_preview_what')}</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((row, index) => (
								<tr key={index}>
									<td>{formatPreviewDate(row.date)}, {row.time}</td>
									<td>{row.text}</td>
								</tr>
							))}
							<tr>
								<td>—</td>
								<td className="text-muted">{t('debt_preview_stop')}</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		)
	}

	function paramsSummary(event) {
		const schema = event.params_schema || {}
		const params = event.params || {}
		return Object.keys(schema)
			.map(name => t('param_' + name) + ': ' + (isHourParam(name) ? hourLabel(params[name]) : params[name]))
			.join(' · ')
	}

	async function saveParams() {
		const response = await POST('/notification-settings/params', {
			'event_code': paramsEvent.code, 'params': paramsDraft,
		}, { loader: true, notification: true })
		if (!httpOk(response)) return

		// В строке показываем то, что сохранил сервер (пустое поле он заменяет значением по умолчанию)
		const saved = response.data.params || paramsDraft
		setEvents(prev => prev.map(e => e.code === paramsEvent.code ? { ...e, 'params': saved } : e))
		setShowParamsModal(false)
	}

	// --- Получатели ---
	async function openRecipients(event) {
		setRecipEvent(event)
		setRecipUsers([])
		setShowRecipModal(true)
		const response = await GET(`/notification-settings/recipients?event_code=${event.code}`, {}, { loader: true })
		if (httpOk(response)) setRecipUsers(response.data.users)
	}

	function toggleRecipUser(id) {
		setRecipUsers(prev => prev.map(u => u.id === id ? { ...u, 'selected': !u.selected } : u))
	}

	async function saveRecipients() {
		const user_ids = recipUsers.filter(u => u.selected).map(u => u.id)
		const response = await POST('/notification-settings/recipients', {
			'event_code': recipEvent.code, 'user_ids': user_ids,
		}, { loader: true, notification: true })
		if (httpOk(response)) setShowRecipModal(false)
	}

	useEffect(() => {
		getData()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	// порядок категорий как пришли события
	const categories = []
	const catMap = {}
	events.forEach(e => {
		if (!catMap[e.group_code]) { catMap[e.group_code] = []; categories.push(e.group_code) }
		catMap[e.group_code].push(e)
	})

	return (
		<>
			<div className="d-flex justify-content-between align-items-center mb-3">
				<h4 className="mb-0">{t('notification_settings')}</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="table-responsive table-sticky-page">
						<table className="table table-bordered align-middle mb-0">
							<thead>
								<tr>
									<th>{t('event')}</th>
									{channels.map((channel, index) => (
										<th key={index} className="text-center" style={{ 'width': '150px' }}>
											{channelLabels[channel] || channel}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{categories.map((cat) => {
									const purpose = categoryPurpose[cat]
									return (
										<React.Fragment key={cat}>
											<tr className="table-light">
												<td className="fw-600">
													{t('group_' + cat)}
													{cat === 'client_sms' &&
														<div className="text-muted" style={{ 'fontSize': '11px', 'fontWeight': 400 }}>
															{sms.is_own_provider
																? `${sms.provider_name} · ${sms.price} ${t('currency_soum')}/SMS · ${t('balance')}: ${Number(sms.balance || 0).toLocaleString()} ≈ ${sms.messages_left} SMS`
																: (sms.provider ? `${sms.provider_name}` : t('sms_provider_not_connected'))}
														</div>
													}
												</td>
												{channels.map((channel, hIdx) => (
													<td key={hIdx} className="text-center">
														{channel === 'tg_group' && purpose &&
															<button type="button"
																className={'btn btn-sm ' + (groupConfigured(purpose) ? 'btn-primary' : 'btn-danger')}
																onClick={() => openGroupModal(purpose)}>
																<i className="uil-cog" /> {groupConfigured(purpose)
													? t('configured') + (poses.length ? ' ' + groupStats(purpose).covered + '/' + groupStats(purpose).total : '')
													: t('not_configured')}
															</button>
														}
													</td>
												))}
											</tr>
											{catMap[cat].map((event, ei) => (
												<tr key={ei}>
													<td>
														<div className="fw-600">{t(event.name_key) || event.code}</div>
														<div className="text-muted" style={{ 'fontSize': '11px' }}>{event.code}</div>
														{event.recipient_type === 'subscription' &&
															<button type="button" className="btn btn-link btn-sm p-0"
																style={{ 'fontSize': '.78rem', 'textDecoration': 'none' }}
																onClick={() => openRecipients(event)}>
																<i className="uil-users-alt" /> {t('recipients')}
															</button>
														}
														{event.recipient_type === 'client' &&
															<button type="button" className="btn btn-link btn-sm p-0"
																style={{ 'fontSize': '.78rem', 'textDecoration': 'none' }}
																onClick={() => openSmsTemplates(event)}>
																<i className="uil-comment-alt-edit" /> {t('sms_text')}
															</button>
														}
														{event.params_schema &&
															<div>
																<button type="button" className="btn btn-link btn-sm p-0"
																	style={{ 'fontSize': '.78rem', 'textDecoration': 'none' }}
																	onClick={() => openParams(event)}>
																	<i className="uil-sliders-v-alt" /> {t('event_params')}
																</button>
																<span className="text-muted ms-2" style={{ 'fontSize': '11px' }}>
																	{paramsSummary(event)}
																</span>
															</div>
														}
													</td>
													{channels.map((channel, cIndex) => {
														if (!channelApplies(event, channel)) {
															return <td key={cIndex}></td>
														}
														const isChanged = cellKey(event.code, channel) in pending
														return (
															<td key={cIndex} className="text-center"
																title={isChanged ? t('unsaved_changes') : undefined}
																style={isChanged ? { 'backgroundColor': 'var(--dark-tint-warning, #fff7e6)' } : undefined}>
																<div className="form-check form-switch form-switch-sm d-inline-block mb-0"
																	style={{ 'paddingLeft': 0, 'minHeight': 'auto' }}>
																	<input type="checkbox" role="switch" className="form-check-input"
																		style={{ 'marginLeft': 0, 'float': 'none', 'cursor': 'pointer' }}
																		checked={cellValue(event, channel)}
																		onChange={(e) => toggle(event, channel, e.target.checked)} />
																</div>
															</td>
														)
													})}
												</tr>
											))}
										</React.Fragment>
									)
								})}
								{events.length === 0 &&
									<tr>
										<td colSpan={channels.length + 1} className="text-center text-muted">{t('list_empty')}</td>
									</tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* Панель сохранения: висит внизу всегда, чтобы кнопку было видно до первой правки */}
			<div className="d-flex justify-content-between align-items-center gap-3 mt-2 p-2 bg-white border rounded shadow-sm"
				style={{ 'position': 'sticky', 'bottom': '10px', 'zIndex': 1000 }}>
				<div className="d-flex align-items-center">
					{hasChanges
						? <>
							<i className="uil-info-circle text-primary me-2" />
							<span className="fw-600">{t('unsaved_changes')}: {pendingKeys.length}</span>
						</>
						: <span className="text-muted">{t('no_changes')}</span>
					}
				</div>
				<div className="d-flex gap-2">
					<button className="btn btn-warning" disabled={saving || !hasChanges} onClick={() => setPending({})}>
						{t('cancel')}
					</button>
					<button className="btn btn-primary" disabled={saving || !hasChanges} onClick={saveChanges}>
						{t('save')}
					</button>
				</div>
			</div>

			{/* MODAL: Telegram-группы категории по филиалам */}
			<Modal show={showGroupModal} onHide={() => setShowGroupModal(false)} centered size="lg">
				<Modal.Header closeButton>
					<Modal.Title style={{ 'fontSize': '1rem' }}>{t('channel_tg_group')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="text-muted mb-2" style={{ 'fontSize': '.82rem' }}>
						{t('tg_chat_id_hint')}
					</div>
					<div className="table-responsive" style={{ 'maxHeight': '60vh' }}>
						<table className="table table-bordered align-middle mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '150px' }}>{t('branch')}</th>
									<th style={{ 'minWidth': '190px' }}>Chat ID</th>
									<th style={{ 'width': '120px' }}>{t('topic')}</th>
									<th style={{ 'width': '60px' }}></th>
								</tr>
							</thead>
							<tbody>
								{groupRows.map((row) => {
									// Филиал с пустым полем получает сообщения в общую группу — это видно сразу,
									// иначе пустая строка читается как «уведомлений не будет»
									const inherits = row.pos_id !== null
										&& String(row.chat_id).trim() === ''
										&& !!String(groupRows[0] ? groupRows[0].chat_id : '').trim()
									return (
										<tr key={row.key} className={row.pos_id === null ? 'table-light' : undefined}>
											<td className="fw-600">
												{row.name}
												{inherits &&
													<div className="text-muted" style={{ 'fontSize': '11px', 'fontWeight': 400 }}>
														{t('all_branches')}
													</div>
												}
											</td>
											<td>
												<input type="text" className="form-control form-control-sm" value={row.chat_id}
													placeholder="-1001234567890"
													onChange={(e) => handleChatIdChange(row, e.target.value)} />
											</td>
											<td>
												<input type="text" className="form-control form-control-sm" value={row.thread_id}
													placeholder="—"
													onChange={(e) => handleThreadIdChange(row, e.target.value)} />
											</td>
											<td className="text-center">
												<button type="button" className="btn btn-sm btn-outline-primary"
													title={t('tg_check')}
													disabled={groupTestingKey === row.key || !String(row.chat_id).trim()}
													onClick={() => testGroupRow(row)}>
													{groupTestingKey === row.key
														? <span className="spinner-border spinner-border-sm" role="status"></span>
														: <i className="uil uil-message"></i>
													}
												</button>
											</td>
										</tr>
									)
								})}
								{groupRows.length === 0 &&
									<tr><td colSpan="4" className="text-center text-muted">{t('list_empty')}</td></tr>
								}
							</tbody>
						</table>
					</div>
					<div className="text-muted mt-2" style={{ 'fontSize': '.78rem' }}>
						{t('tg_thread_id_hint')}
					</div>
					<div className="d-flex gap-2 w-100 mt-3">
						<button className="btn btn-warning w-100" onClick={() => setShowGroupModal(false)}>{t('cancel')}</button>
						<button className="btn btn-primary w-100" onClick={saveGroups}>{t('save')}</button>
					</div>
				</Modal.Body>
			</Modal>

			{/* MODAL: Параметры события */}
			{/* Высота фиксированная, прокручивается только тело: шапка и кнопки остаются на месте */}
			<Modal show={showParamsModal} onHide={() => setShowParamsModal(false)} centered scrollable>
				<Modal.Header closeButton>
					<Modal.Title style={{ 'fontSize': '1rem' }}>
						{t('event_params')}{paramsEvent ? ' — ' + (t(paramsEvent.name_key) || paramsEvent.code) : ''}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ 'height': '500px' }}>
					{paramsEvent &&
						<div className="text-muted mb-3" style={{ 'fontSize': '.82rem' }}>
							{t('params_hint_' + paramsEvent.code.replace(/\./g, '_'))}
						</div>
					}
					{/* Параметры — короткие поля по два в ряд, пояснение — «?» у подписи, а не текстом под полем */}
					<div className="row g-3">
						{paramsEvent && Object.keys(paramsEvent.params_schema || {}).map((name) => {
							const spec = paramsEvent.params_schema[name]
							return (
								// Подпись может уйти в две строки (узбекский длиннее) — поле прижато вниз,
								// чтобы поля одного ряда стояли на одной линии
								<div className="col-6 d-flex flex-column" key={name}>
									<label>
										{t('param_' + name)}
										<HintIcon text={isHourParam(name)
											? t('param_' + name + '_hint')
											: t('param_' + name + '_hint') + ' (' + spec.min + '–' + spec.max + ')'} />
									</label>
									{isHourParam(name)
										? <select className="form-select mt-auto"
											value={paramsDraft[name] ?? spec.default}
											onChange={(e) => setParamsDraft({ ...paramsDraft, [name]: Number(e.target.value) })}>
											{paramHours(spec).map(hour => (
												<option key={hour} value={hour}>{hourLabel(hour)}</option>
											))}
										</select>
										: <input type="number" className="form-control mt-auto"
											min={spec.min} max={spec.max} step="1"
											value={paramsDraft[name] ?? ''}
											onChange={(e) => setParamsDraft({ ...paramsDraft, [name]: e.target.value })} />
									}
								</div>
							)
						})}
					</div>
					{paramsEvent && paramsEvent.code === 'debt.client.reminder' && debtReminderPreview()}
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex gap-2 w-100">
						<button className="btn btn-warning w-100" onClick={() => setShowParamsModal(false)}>{t('cancel')}</button>
						<button className="btn btn-primary w-100" onClick={saveParams}>{t('save')}</button>
					</div>
				</Modal.Footer>
			</Modal>

			{/* MODAL: Получатели */}
			<Modal show={showRecipModal} onHide={() => setShowRecipModal(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title style={{ 'fontSize': '1rem' }}>
						{t('recipients')}{recipEvent ? ' — ' + (t(recipEvent.name_key) || recipEvent.code) : ''}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="text-muted mb-2" style={{ 'fontSize': '.82rem' }}>
						{t('channel_bell')} / {t('channel_firebase')}
					</div>
					<div style={{ 'maxHeight': '340px', 'overflowY': 'auto' }}>
						{recipUsers.map((u, index) => (
							<div key={index}
								className="d-flex align-items-center justify-content-between py-2 px-1"
								style={{ 'cursor': 'pointer', 'borderBottom': '1px solid var(--dark-border, #f1f2f4)' }}
								onClick={() => toggleRecipUser(u.id)}>
								<div>
									<span className="fw-600">{u.name}</span>
									{u.position_name &&
										<span className="text-muted ms-2" style={{ 'fontSize': '.8rem' }}>{u.position_name}</span>
									}
								</div>
								<input type="checkbox" className="form-check-input mt-0"
									checked={!!u.selected} onChange={() => { }} />
							</div>
						))}
						{recipUsers.length === 0 &&
							<div className="text-center text-muted py-3">{t('list_empty')}</div>
						}
					</div>
					<div className="d-flex gap-2 w-100 mt-3">
						<button className="btn btn-warning w-100" onClick={() => setShowRecipModal(false)}>{t('cancel')}</button>
						<button className="btn btn-primary w-100" onClick={saveRecipients}>{t('save')}</button>
					</div>
				</Modal.Body>
			</Modal>

			{/* MODAL: тексты SMS гостю */}
			<Modal show={showSmsModal} onHide={() => setShowSmsModal(false)} centered size="lg">
				<Modal.Header closeButton>
					<Modal.Title style={{ 'fontSize': '1rem' }}>
						{t('sms_text')}{smsEvent ? ' — ' + (t(smsEvent.name_key) || smsEvent.code) : ''}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="text-muted mb-2" style={{ 'fontSize': '.82rem' }}>
						{t('sms_text_hint')}
					</div>
					<div className="text-muted mb-2" style={{ 'fontSize': '.82rem' }}>
						{t('sms_text_channels_hint')}
					</div>

					{/* Язык гостя берётся из его карточки: русский и узбекский — это почти вся база,
					    поэтому текст задаётся на каждый язык отдельно */}
					<ul className="nav nav-tabs mb-2">
						{smsTemplates.map((item, index) => (
							<li className="nav-item" key={index}>
								<button type="button"
									className={'nav-link ' + (smsLocale === item.locale ? 'active' : '')}
									onClick={() => setSmsLocale(item.locale)}>
									{t('lang_' + item.locale)}
									{item.body ? <i className="uil-check text-success ms-1" /> : null}
								</button>
							</li>
						))}
					</ul>

					{/* Пока тексты собираем мы: клиент их только читает. Показывать всё равно
						нужно — от его имени уходит платное сообщение, и он должен видеть,
						что именно получит гость и во сколько сегментов это уложится. */}
					<div className="form-group">
						<textarea className="form-control" rows="4" disabled
							value={smsTemplateBody(smsLocale) || smsTemplateDefault(smsLocale)}
							readOnly />
						<div className="d-flex justify-content-between mt-1">
							<span className="text-muted" style={{ 'fontSize': '.78rem' }}>
								<i className="uil-lock" /> {t('sms_template_readonly')}
							</span>
							<span className="text-muted" style={{ 'fontSize': '.78rem' }}>
								{smsPriceHint(smsTemplateBody(smsLocale) || smsTemplateDefault(smsLocale))}
							</span>
						</div>
					</div>

					<div className="d-flex gap-2 w-100 mt-3">
						<button className="btn btn-primary w-100" onClick={() => setShowSmsModal(false)}>{t('close')}</button>
					</div>
				</Modal.Body>
			</Modal>
		</>
	)
}

export default Index
